import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { loadMoreDataWhenScrolled, parseHeaderForLinks } from 'react-jhipster';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ILogisticRoute, ILogisticRouteListItem, ILogisticRoutePreviewRequest, ILogisticRoutePreviewResponse, defaultValue } from 'app/shared/model/logistic-route.model';

interface LogisticRouteState extends EntityState<ILogisticRoute> {
  preview: ILogisticRoutePreviewResponse | null;
  previewLoading: boolean;
  routeList: ILogisticRouteListItem[];
  routeListLoading: boolean;
}

const initialState: LogisticRouteState = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  links: { next: 0 },
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  preview: null,
  previewLoading: false,
  routeList: [],
  routeListLoading: false,
};

const apiUrl = 'api/logistic-routes';

export const getEntities = createAsyncThunk('logisticRoute/fetch_entity_list', async ({ page, size, sort, companyId }: any) => {
  const base = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  const url = companyId != null ? `${base}&companyId=${companyId}` : base;
  return axios.get<ILogisticRoute[]>(url);
});

export const getEntity = createAsyncThunk(
  'logisticRoute/fetch_entity',
  async (id: string | number) => {
    return axios.get<ILogisticRoute>(`${apiUrl}/${id}`);
  },
  { serializeError: serializeAxiosError }
);

export const previewRoute = createAsyncThunk(
  'logisticRoute/preview',
  async (request: ILogisticRoutePreviewRequest) => {
    return axios.post<ILogisticRoutePreviewResponse>(`${apiUrl}/preview`, request);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'logisticRoute/create_entity',
  async (entity: ILogisticRoute) => {
    return axios.post<ILogisticRoute>(apiUrl, entity);
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'logisticRoute/update_entity',
  async (entity: ILogisticRoute) => {
    return axios.put<ILogisticRoute>(`${apiUrl}/${entity.id}`, entity);
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'logisticRoute/partial_update_entity',
  async (entity: ILogisticRoute) => {
    return axios.patch<ILogisticRoute>(`${apiUrl}/${entity.id}`, entity);
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'logisticRoute/delete_entity',
  async (id: string | number) => {
    return await axios.delete<ILogisticRoute>(`${apiUrl}/${id}`);
  },
  { serializeError: serializeAxiosError }
);

export const assignRoute = createAsyncThunk(
  'logisticRoute/assign_route',
  async ({ routeId, truckImplementId }: { routeId: number; truckImplementId: number }) => {
    return axios.post(`${apiUrl}/${routeId}/assign/${truckImplementId}`);
  },
  { serializeError: serializeAxiosError }
);

export const unassignRoute = createAsyncThunk(
  'logisticRoute/unassign_route',
  async (truckImplementId: number) => {
    return axios.delete(`${apiUrl}/unassign/${truckImplementId}`);
  },
  { serializeError: serializeAxiosError }
);

export const getActiveRoute = createAsyncThunk(
  'logisticRoute/get_active_route',
  async (truckImplementId: number) => {
    return axios.get(`api/truck-implements/${truckImplementId}/active-logistic-route`);
  },
  { serializeError: serializeAxiosError }
);

export const getRouteList = createAsyncThunk(
  'logisticRoute/fetch_route_list',
  async (companyId?: number | null) => {
    const params = companyId != null ? `?companyId=${companyId}` : '';
    return axios.get<ILogisticRouteListItem[]>(`${apiUrl}/list${params}`);
  },
  { serializeError: serializeAxiosError }
);

export const LogisticRouteSlice = createSlice({
  name: 'logisticRoute',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = defaultValue;
      })
      .addMatcher(isPending(previewRoute), state => {
        state.previewLoading = true;
        state.errorMessage = null;
        state.preview = null;
      })
      .addMatcher(isFulfilled(previewRoute), (state, action) => {
        state.previewLoading = false;
        state.preview = action.payload.data;
      })
      .addMatcher(isRejected(previewRoute), (state, action) => {
        state.previewLoading = false;
        state.errorMessage = action.error.message ?? 'Erro ao calcular rota';
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;
        const links = parseHeaderForLinks(headers.link);
        return {
          ...state,
          loading: false,
          links,
          entities: loadMoreDataWhenScrolled(state.entities as ILogisticRoute[], data, links),
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isFulfilled(assignRoute, unassignRoute), state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(assignRoute, unassignRoute), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getRouteList), state => {
        state.routeListLoading = true;
        state.errorMessage = null;
      })
      .addMatcher(isFulfilled(getRouteList), (state, action) => {
        state.routeListLoading = false;
        state.routeList = action.payload.data;
      })
      .addMatcher(isRejected(getRouteList), state => {
        state.routeListLoading = false;
      })
      .addMatcher(
        action => action.type.startsWith('logisticRoute/') && action.type.endsWith('/rejected') && !action.type.includes('preview'),
        (state, action: any) => {
          if (action.error?.name === 'CanceledError') {
            state.loading = false;
            state.updating = false;
            return;
          }
          state.loading = false;
          state.updating = false;
          state.updateSuccess = false;
          state.errorMessage = action.error.message;
        }
      );
  },
});

export const { reset } = LogisticRouteSlice.actions;

export default LogisticRouteSlice.reducer;
